import React from 'react';
import styled from 'styled-components';
import { FieldNewsDto } from '../../services/fieldNewsService';

interface NewsDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  news: FieldNewsDto | null;
}

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #0a0f0a 0%, #1a2a1a 100%);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 15px;
  max-width: 800px;
  max-height: 90vh;
  width: 100%;
  overflow-y: auto;
  position: relative;

  @media (max-width: 768px) {
    max-width: 95vw;
    max-height: 95vh;
    border-radius: 12px;
  }
`;

const ModalHeader = styled.div`
  background: rgba(0, 212, 255, 0.1);
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  padding: 20px 30px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 10;

  @media (max-width: 768px) {
    padding: 15px 20px;
  }
`;

const ModalTitle = styled.h2<{ $isPriority?: boolean }>`
  color: ${props => props.$isPriority ? '#ffaa00' : '#00d4ff'};
  margin: 0;
  font-size: 1.5rem;
  font-weight: bold;
  padding-right: 40px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.3rem;
    padding-right: 30px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #00d4ff;
  }
`;

const PriorityBadge = styled.div`
  position: absolute;
  top: 15px;
  right: 60px;
  background: linear-gradient(135deg, #ffaa00, #ff8800);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: bold;
  letter-spacing: 0.5px;
  text-transform: uppercase;

  @media (max-width: 768px) {
    top: 12px;
    right: 50px;
    padding: 4px 8px;
    font-size: 0.7rem;
  }
`;

const ModalBody = styled.div`
  padding: 30px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const NewsImage = styled.img`
  width: 100%;
  max-height: 400px;
  object-fit: contain;
  border-radius: 10px;
  margin-bottom: 20px;
  border: 1px solid rgba(0, 212, 255, 0.2);
  background: rgba(0, 0, 0, 0.2);
`;

const NewsContent = styled.div`
  color: #ccc;
  line-height: 1.7;
  font-size: 1rem;
  margin-bottom: 25px;
  white-space: pre-wrap;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const NewsMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.9rem;
  color: #888;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetaLabel = styled.span`
  color: #b0b0b0;
  font-weight: bold;
`;

const NewsDetailModal: React.FC<NewsDetailModalProps> = ({ isOpen, onClose, news }) => {
  if (!news) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getImageUrl = (): string | undefined => {
    if (news.imageUrls && news.imageUrls.length > 0) {
      return news.imageUrls[0];
    }
    return news.imageUrl;
  };

  return (
    <ModalOverlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle $isPriority={news.isPriority}>
            {news.title}
          </ModalTitle>
          {news.isPriority && (
            <PriorityBadge>
              Priority
            </PriorityBadge>
          )}
          <CloseButton onClick={onClose}>
            ×
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {getImageUrl() && (
            <NewsImage 
              src={getImageUrl()} 
              alt={news.title}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          )}

          <NewsContent>
            {news.content}
          </NewsContent>

          <NewsMeta>
            <MetaItem>
              <MetaLabel>Author:</MetaLabel>
              <span>{news.authorName}</span>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Published:</MetaLabel>
              <span>{formatDate(news.publishedDate || news.createdDate)}</span>
            </MetaItem>
            {news.createdDate !== news.updatedDate && (
              <MetaItem>
                <MetaLabel>Updated:</MetaLabel>
                <span>{formatDate(news.updatedDate)}</span>
              </MetaItem>
            )}
          </NewsMeta>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default NewsDetailModal;